import { useEffect, useRef } from 'react';
import { data, Link } from 'react-router';
import {
  ArrowLeft, Calendar, Clock, Cuboid, Download, Eye, FileDown, FileText,
  Link2, Printer, Shapes, Share2, Tag } from 'lucide-react';
import type { Route } from './+types/artikel';
import { PublicShell } from '~/components/public/PublicLayout';
import { ArticleToc } from '~/components/public/ArticleToc';
import { ReadingProgress } from '~/components/public/ReadingProgress';
import { ImageLightbox } from '~/components/public/ImageLightbox';
import { CodeCopyButtons } from '~/components/public/CodeCopyButtons';
import { ReaderControls, useReader } from '~/components/public/ReaderControls';
import { FeedbackFooter } from '~/components/public/FeedbackFooter';
import { PathRail } from '~/components/public/PathRail';
import { PathStepBar } from '~/components/public/PathStepBar';
import { SearchTermHighlight } from '~/components/public/SearchTermHighlight';
import { seoMeta } from '~/lib/seo';
import { formatFileSize, readingLabel, isImageFile, isVideoFile } from '~/lib/format';
import type { ArticlePageData, SeoLocals } from '#shared/types/article';

export async function loader({ params, request, context }: Route.LoaderArgs) {
  // ?pad=<leerpad-slug> zegt: deze lezer volgt een leerpad. De canonical wordt
  // zonder querystring gebouwd (utils/seo-helpers.js), dus dit levert geen
  // tweede pagina op in de zoekmachine.
  const url = new URL(request.url);
  const pad = url.searchParams.get('pad');
  const payload = await context.services.articlePayload(params.slug, pad);
  if (!payload) {
    // De foutpagina (root.tsx) toont deze uitwegen: artikelen die lijken op
    // wat er in de URL stond. Een verhuisd of hernoemd artikel is dan één klik
    // weg in plaats van een doodlopende straat.
    throw data(
      { message: 'Artikel niet gevonden', suggesties: context.services.notFoundSuggestions(url.pathname) },
      { status: 404 },
    );
  }
  // ?q= komt mee vanuit de zoekresultaten en kleurt de zoekwoorden in de
  // tekst (SearchTermHighlight). Hoort niet bij de identiteit van de pagina:
  // de canonical blijft kaal, en de service worker cachet op het pad alleen.
  return data({ ...payload, q: url.searchParams.get('q')?.trim() || null }, {
    headers: payload.article.updatedAt
      ? { 'Last-Modified': new Date(payload.article.updatedAt).toUTCString() }
      : undefined,
  });
}

export function meta({ data: d }: Route.MetaArgs) {
  // seoMeta levert ook de canonical als {tagName:'link'}-descriptor.
  return seoMeta(d?.seo as SeoLocals | undefined);
}

// private: de feedbackvoet toont de duim die deze bezoeker zelf koos (uit de
// stemcookie), dus deze HTML verschilt per bezoeker. Anders dan de homepagina
// mag een gedeelde cache hem niet aan de volgende doorgeven.
export function headers() {
  return { 'Cache-Control': 'private, no-cache' };
}

export default function ArtikelPage({ loaderData }: Route.ComponentProps) {
  const { article, related, theme, seo, pathContext, q } = loaderData;
  const pageRef = useRef<HTMLDivElement>(null);
  const proseRef = useRef<HTMLElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const base = `/artikel/${encodeURIComponent(article.slug)}`;
  const reader = useReader();

  // Weergave tellen — herstelt het dode [data-viewcount]-mechanisme van de
  // oude pagina: daar werd de POST nooit gedaan, dus artikelweergaven werden
  // publiek niet geteld. Eén keer per paginabezoek.
  useEffect(() => {
    fetch(`/api/stats/view/${encodeURIComponent(article.id)}`, { method: 'POST' }).catch(() => {});
  }, [article.id]);

  const imageAttachments = article.attachments.filter((a) => isImageFile(a.filename || a.url));
  const fileAttachments = article.attachments.filter(
    (a) => !isImageFile(a.filename || a.url) && !isVideoFile(a.filename || a.url),
  );
  const videos = [
    ...article.attachments.filter((a) => isVideoFile(a.filename || a.url)).map((a) => ({ src: a.url, name: a.filename })),
    ...article.videos.map((v) => ({ src: v.src || v.url || '', name: v.name || 'Video' })),
  ].filter((v) => v.src);

  const share = async () => {
    const url = seo?.canonical || location.href;
    try {
      if (navigator.share) await navigator.share({ title: article.name, url });
      else await navigator.clipboard.writeText(url);
    } catch { /* geannuleerd */ }
  };
  const copyLink = () => navigator.clipboard.writeText(seo?.canonical || location.href).catch(() => {});

  return (
    <div ref={pageRef}>
      <ReadingProgress />
      <PublicShell>
      {/* De <main> is precies zo breed als het paar zijbalk + leesmaat
          (280 + 32 gap + 64ch), niet de volle 1280px. Zo staan de broodkruimel
          en de kolommen eronder op dezelfde lijn — met alleen justify-center op
          het grid hing de broodkruimel 200px links van de zijbalk. */}
      <main
        id="main"
        tabIndex={-1}
        /* 1180px is een VAST getal en bewust geen calc() met
           var(--container-prose) erin. Die variabele is 64ch, en de ch-eenheid
           rekent tegen de font-size van het element waarin hij valt: op #main
           is dat de basisschaal (14px), in .kb-prose 17px. Diezelfde variabele
           levert op die twee plekken dus verschillende pixels op, en de
           container kneep de leeskolom daardoor stilletjes naar 601px terwijl
           .kb-prose zelf op ~675px mikte.
           1180 = 40 (px-5) + 352 (zijbalk) + 48 (gap-12) + 740 (kaart), en die
           kaart is 80 padding + ~660 tekst — net binnen de 64ch. */
        // pb-16 plus de hoogte van de leerpadbalk, anders valt de laatste
        // alinea erachter. De variabele is 0px op elke pagina zonder balk.
        className="mx-auto max-w-[1180px] px-5 pt-6 pb-[calc(4rem+var(--kb-stepbar-h,0px))] focus:outline-none"
      >
        {/* broodkruimel */}
        <nav aria-label="Broodkruimelpad" className="mb-5">
          <ol className="flex flex-wrap items-center gap-1.5 text-sm text-ink-3">
            <li><Link to="/" viewTransition className="transition hover:text-ink">Kennisbank</Link></li>
            {/* Categorie als die er is, anders het thema. Zonder die terugval
                miste 66% van de artikelen deze kruimel volledig — thema's
                dekken 99% van het corpus, categorieën 34%. */}
            {article.categories[0] ? (
              <>
                <li aria-hidden>/</li>
                <li>
                  <Link
                    to={`/search?categories=${encodeURIComponent(article.categories[0])}`}
                    viewTransition
                    className="transition hover:text-ink"
                  >
                    {article.categories[0]}
                  </Link>
                </li>
              </>
            ) : theme ? (
              <>
                <li aria-hidden>/</li>
                <li>
                  <Link to={`/thema/${theme.slug}`} viewTransition className="transition hover:text-ink">
                    {theme.name}
                  </Link>
                </li>
              </>
            ) : null}
            <li aria-hidden>/</li>
            {/* min-w-0: zonder dat weigert dit flexitem onder zijn min-content
                te krimpen — en `truncate` zet white-space:nowrap, dus die
                min-content is de VOLLEDIGE titel. Op 360px duwde dat de
                broodkruimel 50px buiten beeld. */}
            <li aria-current="page" className="min-w-0 max-w-[40ch] truncate text-ink-2">{article.name}</li>
          </ol>
        </nav>

        {pathContext && <PathRail ctx={pathContext} />}

        {/* De tekstbaan vult wat er na de zijbalk overblijft — en omdat <main>
            hierboven al op de leesmaat is begrensd, is dat exact 64ch. Met de
            oude 1fr binnen een 1280px-container was de kolom 928px voor een
            tekstblok van 674px: de border-b onder de artikelkop liep 213px
            voorbij het einde van de regel eronder, en dát leest als slordig.
            minmax(0,1fr) laat de baan bij 1024px netjes krimpen. */}
        {/* Zonder zijbalk zakt de tekstbaan naar één kolom; de leesmaat op
            <main> houdt de regellengte gelijk, dus de tekst springt niet
            breder als je de inhoudsopgave wegklikt. */}
        {/* grid-cols-[minmax(0,1fr)] is GEEN ruis: een impliciete `auto`-kolom
            krijgt als ondergrens de min-content van haar breedste item, en die
            werd hier bepaald door één `truncate`-regel in de zijbalk (nowrap =
            min-content is de hele zin). Op een 360px-scherm blies dat de kolom
            op tot ~1990px: de héle artikelpagina werd twee keer zo breed als
            het scherm en je moest per regel heen en weer vegen om te lezen.
            minmax(0,1fr) legt de ondergrens op nul, dus de kolom kan nooit meer
            breder worden dan het scherm — ongeacht wat erin staat. */}
        <div className={`grid grid-cols-[minmax(0,1fr)] gap-12 ${reader.tocVisible ? 'lg:grid-cols-[22rem_minmax(0,1fr)]' : 'lg:grid-cols-1'}`}>
          {/* zijbalk */}
          <aside className={`order-2 min-w-0 lg:order-1 ${reader.tocVisible ? '' : 'lg:hidden'}`}>
            <div className="space-y-4 lg:sticky lg:top-24">
              <Link
                to="/"
                viewTransition
                className="flex w-full items-center justify-center gap-2 rounded-md border border-hairline-strong px-4 py-2 text-base font-medium text-ink-2 transition hover:border-accent hover:text-ink"
              >
                <ArrowLeft size={15} aria-hidden /> Terug naar overzicht
              </Link>

              <section className="rounded-lg border border-hairline bg-surface p-4 shadow-sm">
                <h2 className="text-2xs font-semibold tracking-wider text-ink-3 uppercase">Inhoudsopgave</h2>
                {(theme || article.categories.length > 0 || article.modules.length > 0) && (
                  <div className="mt-2 mb-3 flex flex-wrap gap-1.5">
                    {theme && (
                      <Link
                        to={`/thema/${theme.slug}`}
                        viewTransition
                        className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2.5 py-0.5 text-xs font-medium text-accent-soft-ink transition hover:bg-accent hover:text-accent-on"
                      >
                        <Shapes size={10} aria-hidden /> {theme.name}
                      </Link>
                    )}
                    {article.categories.map((c) => (
                      <span key={c} className="inline-flex items-center gap-1 rounded-full bg-sunken px-2.5 py-0.5 text-xs font-medium text-ink-2">
                        <Tag size={10} aria-hidden /> {c}
                      </span>
                    ))}
                    {article.modules.map((m) => (
                      <span key={m} className="inline-flex items-center gap-1 rounded-full bg-sunken px-2.5 py-0.5 text-xs font-medium text-ink-2">
                        <Cuboid size={10} aria-hidden /> {m}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-2">
                  <ArticleToc articleRef={proseRef} />
                </div>
              </section>

              {videos.length > 0 && (
                <section className="rounded-lg border border-hairline bg-surface p-4 shadow-sm">
                  <h2 className="text-2xs font-semibold tracking-wider text-ink-3 uppercase">Video&apos;s</h2>
                  <div className="mt-3 space-y-3">
                    {videos.map((v, i) => (
                      <div key={i}>
                        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- bron-video's hebben geen ondertitelbestanden */}
                        <video src={v.src} controls preload="metadata" playsInline className="w-full rounded-md" />
                        <div className="mt-1 flex items-center justify-between gap-2 text-sm text-ink-3">
                          <span className="truncate">{v.name}</span>
                          <a
                            href={`${base}/download?url=${encodeURIComponent(v.src)}`}
                            className="shrink-0 text-ink hover:underline"
                          >
                            Download
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {imageAttachments.length > 0 && (
                <section className="rounded-lg border border-hairline bg-surface p-4 shadow-sm">
                  <h2 className="text-2xs font-semibold tracking-wider text-ink-3 uppercase">
                    Afbeeldingen ({imageAttachments.length})
                  </h2>
                  <ul className="mt-3 space-y-2">
                    {imageAttachments.map((a) => (
                      <li key={a.url} className="flex items-center gap-3">
                        <button
                          type="button"
                          data-lightbox={a.url}
                          data-lightbox-name={a.filename}
                          className="h-12 w-16 shrink-0 overflow-hidden rounded-md border border-hairline"
                          aria-label={`Bekijk ${a.filename}`}
                        >
                          <img src={a.url} alt="" className="h-full w-full object-cover transition hover:opacity-80" loading="lazy" />
                        </button>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm text-ink-2" title={a.filename}>{a.filename}</p>
                          <p className="text-xs text-ink-3">{formatFileSize(a.size)}</p>
                        </div>
                        <a
                          href={`${base}/download?url=${encodeURIComponent(a.url)}`}
                          className="shrink-0 rounded-md p-1.5 text-ink-3 transition hover:bg-surface-hover hover:text-ink"
                          aria-label={`Download ${a.filename}`} title="Download"
                        >
                          <Download size={15} />
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {fileAttachments.length > 0 && (
                <section className="rounded-lg border border-hairline bg-surface p-4 shadow-sm">
                  <h2 className="text-2xs font-semibold tracking-wider text-ink-3 uppercase">
                    Bijlagen ({fileAttachments.length})
                  </h2>
                  <ul className="mt-3 space-y-2">
                    {fileAttachments.map((a) => (
                      <li key={a.url} className="flex items-center gap-3">
                        <FileText size={18} className="shrink-0 text-ink-3" aria-hidden />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm text-ink-2" title={a.filename}>{a.filename}</p>
                          <p className="text-xs text-ink-3">{formatFileSize(a.size)}</p>
                        </div>
                        <a
                          href={`${base}/view?url=${encodeURIComponent(a.url)}`}
                          className="shrink-0 rounded-md p-1.5 text-ink-3 transition hover:bg-surface-hover hover:text-ink"
                          aria-label={`Bekijk ${a.filename}`} title="Bekijken"
                        >
                          <Eye size={15} />
                        </a>
                        <a
                          href={`${base}/download?url=${encodeURIComponent(a.url)}`}
                          className="shrink-0 rounded-md p-1.5 text-ink-3 transition hover:bg-surface-hover hover:text-ink"
                          aria-label={`Download ${a.filename}`} title="Download"
                        >
                          <Download size={15} />
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {related.length > 0 && (
                <section className="rounded-lg border border-hairline bg-surface p-4 shadow-sm">
                  <h2 className="text-2xs font-semibold tracking-wider text-ink-3 uppercase">Gerelateerde artikelen</h2>
                  <ul className="mt-3 space-y-1">
                    {related.map((r) => (
                      <li key={r.id}>
                        <Link
                          viewTransition
                          prefetch="intent"
                          to={`/artikel/${encodeURIComponent(r.slug || r.id)}`}
                          className="block rounded-md px-2 py-1.5 transition hover:bg-surface-hover"
                        >
                          <span className="block text-base font-medium text-ink">{r.name}</span>
                          {r.subject && <span className="block truncate text-sm text-ink-3">{r.subject}</span>}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              <section className="rounded-lg border border-hairline bg-surface p-4 shadow-sm">
                <h2 className="text-2xs font-semibold tracking-wider text-ink-3 uppercase">Acties</h2>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <a
                    href={`${base}/pdf`}
                    className="flex items-center justify-center gap-1.5 rounded-md border border-hairline-strong px-2 py-2 text-base font-medium text-ink-2 transition hover:border-accent hover:text-ink"
                  >
                    <FileDown size={14} aria-hidden /> PDF
                  </a>
                  <button
                    type="button" onClick={() => window.print()}
                    className="flex items-center justify-center gap-1.5 rounded-md border border-hairline-strong px-2 py-2 text-base font-medium text-ink-2 transition hover:border-accent hover:text-ink"
                  >
                    <Printer size={14} aria-hidden /> Print
                  </button>
                  <button
                    type="button" onClick={share}
                    className="flex items-center justify-center gap-1.5 rounded-md border border-hairline-strong px-2 py-2 text-base font-medium text-ink-2 transition hover:border-accent hover:text-ink"
                  >
                    <Share2 size={14} aria-hidden /> Deel
                  </button>
                  <button
                    type="button" onClick={copyLink}
                    className="flex items-center justify-center gap-1.5 rounded-md border border-hairline-strong px-2 py-2 text-base font-medium text-ink-2 transition hover:border-accent hover:text-ink"
                  >
                    <Link2 size={14} aria-hidden /> Kopieer
                  </button>
                </div>
              </section>
            </div>
          </aside>

          {/* artikel — in volledig scherm wordt dit een eigen laag over de
              pagina heen, met de leesmaat gecentreerd.

              Buiten volledig scherm staat de tekst op een WITTE kaart. Dat was
              hij niet: de zijbalkblokken hiernaast waren bg-surface, maar de
              artikeltekst zelf lag kaal op het grijze canvas — het enige stuk
              inhoud in de hele app zonder witte drager, en precies daarom las
              een artikel als "grijzig".

              Op mobiel loopt de kaart van rand tot rand (-mx-5 heft de px-5
              van de pagina op) met alleen een lijn boven en onder; vanaf sm
              wordt het een echte kaart. Een kaart mét zijmarges op 375px zou
              44px aan witruimte links en rechts kosten en de leeskolom
              onwerkbaar smal maken. */}
          <article
            className={
              reader.fullscreen
                ? 'fixed inset-0 z-200 mx-auto overflow-y-auto bg-surface px-5 py-10 [&>*]:mx-auto [&>*]:max-w-[var(--container-prose)]'
                : 'order-1 -mx-5 min-w-0 border-y border-hairline bg-surface px-5 py-7 shadow-sm sm:mx-0 sm:rounded-lg sm:border sm:px-10 lg:order-2'
            }
          >
            <header className="mb-6 border-b border-hairline pb-6">
              <h1 className="font-display text-3xl font-semibold tracking-[-0.02em] text-ink">
                {article.name}
              </h1>
              {article.subject && article.subject !== article.name && (
                <p className="mt-2 text-xl text-ink-2">{article.subject}</p>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-ink-3">
                <span className="flex items-center gap-1.5">
                  <Calendar size={13} aria-hidden /> Bijgewerkt: {article.updatedAtStr}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock size={13} aria-hidden /> {readingLabel(article.readingMinutes)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Eye size={13} aria-hidden /> {article.views}× bekeken
                </span>
              </div>
            </header>

            <section
              ref={proseRef}
              className="kb-prose"
              // Alleen de leesmaat schaalt mee, niet de koppen en de code —
              // zie ReaderControls voor waarom dat niet de font-size van het
              // hele artikel mag zijn.
              style={{ fontSize: `calc(var(--text-prose) * ${reader.scale})` }}
              // Server-side gesaneerd (sanitizeArticle) — dezelfde grens als de EJS-app.
              dangerouslySetInnerHTML={{ __html: article.html }}
            />

            {/* Het sein "einde van de tekst in beeld" voor het automatisch
                afvinken. Een broer van .kb-prose en niet iets dat erin wordt
                geprikt: React beheert die subtree via dangerouslySetInnerHTML
                en gooit een aangehangen kind weg zodra de html verandert. */}
            <div ref={endRef} aria-hidden className="h-px w-full" />

            <FeedbackFooter
              articleId={article.id}
              up={article.feedback.up}
              down={article.feedback.down}
              mine={article.feedback.mine}
            />

            {related.length > 0 && (
              /* kb-no-print: op papier is een raster met doorklikkaarten dood
                 gewicht — de lezer kan er niet op klikken. */
              <section className="kb-no-print mt-10">
                <h2 className="font-display text-xl font-semibold text-ink">Gerelateerde artikelen</h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {related.map((r) => (
                    <Link
                      viewTransition
                      key={r.id}
                      to={`/artikel/${encodeURIComponent(r.slug || r.id)}`}
                      className="group rounded-lg border border-hairline bg-surface p-4 shadow-sm transition hover:border-accent hover:shadow-glow"
                    >
                      <span className="block text-lg font-semibold text-ink transition group-hover:text-ink">{r.name}</span>
                      {r.subject && <span className="mt-1 block truncate text-sm text-ink-3">{r.subject}</span>}
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>
      </PublicShell>

      <ImageLightbox scopeRef={pageRef} downloadBase={base} />
      <CodeCopyButtons scopeRef={proseRef} />
      <SearchTermHighlight q={q} scopeRef={proseRef} />
      <ReaderControls reader={reader} onShare={share} />
      {pathContext?.active && (
        <PathStepBar
          ctx={pathContext}
          articleId={article.id}
          readingMinutes={article.readingMinutes}
          sentinelRef={endRef}
          // Volledig scherm is bewust een kale-tekst-modus; de balk hoort daar
          // niet, en de ruimte die hij reserveert dus ook niet.
          hidden={reader.fullscreen}
        />
      )}
    </div>
  );
}
